import PropTypes from 'prop-types'
import { ChevronDown, ChevronUp, Trash } from 'react-feather'
import { Col, Collapse, FormGroup, Input, Label, Row } from 'reactstrap'
import CustomButton from 'components/Buttons/CustomButton'
import PMBInputAmount from 'components/PMBInputAmount'
import PMBSelect from 'components/PMBSelect'
import RoundedComponent from 'components/Rounded'

const TransferInventoryItem = ({
  item,
  index,
  options = [],
  onSelect,
  onRemove,
  onAmountChange,
  onGroupItemAmountChange,
  onToggleGroup,
  formatAmount,
  intl,
}) => {
  // If item is empty / unselected slot
  if (!item || item === '' || !item.id) {
    return (
      <div
        className="bg-light p-3 rounded border border-gray-200 mb-3"
        data-testid={`tf-inv-form-item-slot-${index}`}
      >
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span
            className="fw-semibold text-secondary"
            style={{ fontSize: '0.9rem' }}
          >
            Pilih Inventori #{index + 1}
          </span>
          <CustomButton
            className="p-1 px-2 d-flex align-items-center"
            color="danger"
            outline
            size="sm"
            onClick={() => onRemove(index)}
            testId={`tf-inv-form-remove-item-${index}`}
            title="Hapus slot"
          >
            <Trash size={16} />
          </CustomButton>
        </div>
        <PMBSelect
          menuPosition="fixed"
          menuPlacement="bottom"
          placeholder="Pilih inventori atau grup inventori..."
          options={options}
          value={null}
          onChange={(selected) => onSelect(index, selected)}
          isSearchable={true}
          filterOption={(option, inputValue) => {
            if (!inputValue) return true
            const q = inputValue.toLowerCase()
            const searchTarget = String(
              option?.data?.searchableText || option?.label || '',
            ).toLowerCase()
            const ownerCode = String(
              option?.data?.ownerCode || '',
            ).toLowerCase()
            const lotCode = String(option?.data?.lotCode || '').toLowerCase()
            return (
              searchTarget.includes(q) ||
              ownerCode.includes(q) ||
              lotCode.includes(q)
            )
          }}
          formatOptionLabel={(data) => {
            const isInventory = data?.amount !== undefined && !data?.isPacking
            const ownerCode = data?.ownerCode
            const lotCode = data?.lotCode
            if (isInventory) {
              return (
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                  <div
                    style={{ width: '100%', wordWrap: 'break-word' }}
                    className="mb-2 mb-md-0"
                  >
                    <div className="fw-medium text-dark">{data.nameItem}</div>
                    <div
                      className="text-muted"
                      style={{ fontSize: '0.85em', marginTop: '2px' }}
                    >
                      <span>Kode Inventori: {ownerCode || '-'}</span>
                      <span className="ms-2 me-2">•</span>
                      <span>Lot ID: {lotCode || '-'}</span>
                    </div>
                  </div>
                  <div
                    className="text-muted d-flex flex-wrap align-items-center justify-content-start justify-content-md-end"
                    style={{ gap: '0.5rem' }}
                  >
                    <span className="badge bg-light text-dark border">
                      {intl.formatNumber(data.amount, {
                        useGrouping: 'always',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      })}{' '}
                      {data.unit}
                    </span>
                  </div>
                </div>
              )
            } else {
              return (
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <span className="fw-medium text-dark">
                      {data.label || data.nameItem}
                    </span>
                    <span className="badge bg-primary-subtle text-primary ms-2">
                      Grup Inventori
                    </span>
                  </div>
                  {data.amount ? (
                    <span className="badge bg-light text-dark border">
                      {intl.formatNumber(data.amount, {
                        useGrouping: 'always',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      })}{' '}
                      {data.unit || ''}
                    </span>
                  ) : null}
                </div>
              )
            }
          }}
          noOptionsMessage={() => 'Tidak ada data yang sesuai'}
          testId={`tf-inv-form-item-select-${index}`}
        />
      </div>
    )
  }

  // If item is a group / packing
  if (item?.isPacking) {
    const isExpanded = item.isExpanded !== false
    const memberItems = item.items || []
    const totalGroupWeight = memberItems.length
      ? memberItems.reduce(
          (acc, curr) =>
            acc + (parseFloat(curr.transferAmount ?? curr.amount) || 0),
          0,
        )
      : item.amount || 0
    const groupUnit = memberItems[0]?.unit || item.unit || ''

    return (
      <div
        className="bg-light p-3 rounded border border-gray-200 mb-3"
        data-testid={`tf-inv-form-item-row-${index}`}
      >
        {/* GROUP HEADER */}
        <div
          className="d-flex justify-content-between align-items-center"
          style={{ minHeight: '42px', cursor: 'pointer' }}
          onClick={() => onToggleGroup(index)}
        >
          <div
            className="d-flex align-items-center flex-grow-1 pe-2 overflow-hidden gap-2"
            style={{ minWidth: 0 }}
          >
            <span
              className="fw-semibold d-block text-truncate"
              style={{
                minWidth: 0,
                maxWidth: '100%',
                lineHeight: 1.2,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }}
              title={item.nameItem || item.label}
            >
              {item.nameItem || item.label}
            </span>
            <span className="badge bg-primary flex-shrink-0">
              {item.loadingItems
                ? 'Memuat...'
                : `${memberItems.length} item(s)`}
            </span>
            <span className="badge bg-secondary text-white flex-shrink-0">
              Grup Inventori
            </span>
          </div>

          <div className="d-flex align-items-center flex-shrink-0 gap-3">
            <div className="text-primary d-flex align-items-center">
              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
            <CustomButton
              className="p-1 px-2 d-flex align-items-center position-relative"
              color="danger"
              outline
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                onRemove(index)
              }}
              testId={`tf-inv-form-remove-item-${index}`}
              title="Hapus grup"
            >
              <Trash size={16} />
            </CustomButton>
          </div>
        </div>

        {/* GROUP SUMMARY */}
        <div className="d-flex justify-content-between align-items-center mt-1">
          <span
            className="fw-semibold text-muted"
            style={{ fontSize: '0.85rem' }}
          >
            Total Berat:{' '}
            {intl.formatNumber(totalGroupWeight, {
              useGrouping: 'always',
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            })}{' '}
            {groupUnit}
          </span>
        </div>

        {/* GROUP MEMBERS COLLAPSE */}
        <Collapse isOpen={isExpanded}>
          <div className="pt-2 mt-2 border-top">
            {item.loadingItems ? (
              <div className="d-flex align-items-center gap-2 p-3 text-muted">
                <span
                  className="spinner-border spinner-border-sm text-primary"
                  role="status"
                />
                <span style={{ fontSize: '0.85rem' }}>
                  Memuat detail inventori dalam grup...
                </span>
              </div>
            ) : memberItems.length > 0 ? (
              memberItems.map((member, memberIdx) => {
                const memberMax = parseFloat(member.amount) || 0
                const rawMemberVal =
                  member.transferAmount !== undefined
                    ? member.transferAmount
                    : member.amount
                const memberVal =
                  rawMemberVal === '' ? '' : parseFloat(rawMemberVal) || 0
                const isOverMax = memberVal !== '' && memberVal > memberMax
                const isUnderMin = memberVal === '' || memberVal <= 0

                return (
                  <div
                    key={member.id || memberIdx}
                    className="bg-white rounded border mb-2 p-3 position-relative"
                  >
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="d-flex gap-2 align-items-center">
                        {member.linkFoto ? (
                          <RoundedComponent
                            childern={
                              <img
                                width={32}
                                height={32}
                                alt={member.nameItem}
                                src={member.linkFoto}
                                style={{
                                  objectFit: 'cover',
                                  borderRadius: '50%',
                                }}
                              />
                            }
                            style={{ width: '32px', height: '32px' }}
                          />
                        ) : null}
                        <div>
                          <span className="fw-semibold d-block">
                            {member.nameItem}
                          </span>
                          <div
                            className="text-muted"
                            style={{ fontSize: '0.8rem', marginTop: '2px' }}
                          >
                            <span>Kode Inventori: {member.ownerCode || '-'}</span>
                            <span className="mx-1">•</span>
                            <span>Lot ID: {member.lotCode || '-'}</span>
                          </div>
                        </div>
                      </div>
                      <span
                        className="badge bg-info text-dark"
                        style={{
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        Grup: {item.nameItem || item.label}
                      </span>
                    </div>

                    <Row className="mt-2">
                      <Col md={6}>
                        <FormGroup className="mb-0">
                          <Label
                            for={`amount-group-${index}-${memberIdx}`}
                            style={{ fontSize: '0.85rem' }}
                          >
                            Berat
                            <span
                              className="mx-1 text-muted"
                              style={{ fontSize: '0.75rem' }}
                            >
                              (Berat maksimal {formatAmount(memberMax)}{' '}
                              {member.unit || ''})
                            </span>
                          </Label>
                          <PMBInputAmount
                            id={`amount-group-${index}-${memberIdx}`}
                            value={
                              memberVal === '' ? '' : formatAmount(memberVal)
                            }
                            onChange={(e) =>
                              onGroupItemAmountChange(
                                index,
                                memberIdx,
                                e.target.value,
                              )
                            }
                            name={`amount-group-${index}-${memberIdx}`}
                            placeholder="Masukkan jumlah"
                          />
                          {isOverMax && (
                            <small className="text-danger d-block mt-1">
                              Jumlah melebihi stok yang tersedia (maksimal{' '}
                              {formatAmount(memberMax)})
                            </small>
                          )}
                          {isUnderMin && (
                            <small className="text-danger d-block mt-1">
                              Jumlah harus lebih dari 0
                            </small>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-0">
                          <Label
                            for={`unit-group-${index}-${memberIdx}`}
                            style={{ fontSize: '0.85rem' }}
                          >
                            Satuan
                          </Label>
                          <Input
                            id={`unit-group-${index}-${memberIdx}`}
                            type="text"
                            disabled
                            value={member.unit || '-'}
                            className="bg-light"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                )
              })
            ) : item.loadError ? (
              <div className="p-3 text-danger small">
                Gagal memuat detail inventori dalam grup. Silakan hapus slot ini
                atau coba pilih kembali.
              </div>
            ) : (
              <div className="p-3 text-muted small">
                Grup inventori ini tidak memiliki anggota inventori.
              </div>
            )}
          </div>
        </Collapse>
      </div>
    )
  }

  // If item is a single inventory
  const maxAmount = parseFloat(item.amount) || 0
  const rawTransfer =
    item.transferAmount !== undefined ? item.transferAmount : item.amount
  const currentTransferAmount =
    rawTransfer === '' ? '' : parseFloat(rawTransfer) || 0
  const isOverMax =
    currentTransferAmount !== '' && currentTransferAmount > maxAmount
  const isUnderMin = currentTransferAmount === '' || currentTransferAmount <= 0

  return (
    <div
      className="bg-light p-3 rounded border border-gray-200 mb-3"
      data-testid={`tf-inv-form-item-row-${index}`}
    >
      {/* ITEM HEADER */}
      <div
        className="d-flex justify-content-between align-items-center"
        style={{ minHeight: '42px' }}
      >
        <div
          className="d-flex align-items-center flex-grow-1 pe-2 overflow-hidden gap-2"
          style={{ minWidth: 0 }}
        >
          <span
            className="fw-semibold d-block text-truncate"
            title={item.nameItem}
          >
            {item.nameItem}
          </span>
          <span className="badge bg-secondary text-white flex-shrink-0">
            Inventori Anda
          </span>
        </div>
        <div className="d-flex align-items-center flex-shrink-0 gap-3">
          <CustomButton
            className="p-1 px-2 d-flex align-items-center position-relative"
            color="danger"
            outline
            size="sm"
            onClick={() => onRemove(index)}
            testId={`tf-inv-form-remove-item-${index}`}
            title="Hapus inventori"
          >
            <Trash size={16} />
          </CustomButton>
        </div>
      </div>

      {/* ITEM CARD DETAILS */}
      <div className="bg-white rounded border mb-2 position-relative p-3 mt-2">
        <div className="d-flex gap-2 align-items-center mb-2">
          {item.linkFoto || item.photo ? (
            <RoundedComponent
              childern={
                <img
                  width={32}
                  height={32}
                  alt={item.nameItem}
                  src={item.linkFoto || item.photo}
                  style={{ objectFit: 'cover', borderRadius: '50%' }}
                />
              }
              style={{ width: '32px', height: '32px' }}
            />
          ) : null}
          <div className="d-flex flex-column">
            <span className="fw-semibold">{item.nameItem}</span>
            <div
              className="text-muted"
              style={{ fontSize: '0.8rem', marginTop: '2px' }}
            >
              <span>Kode Inventori: {item.ownerCode || '-'}</span>
              <span className="mx-1">•</span>
              <span>Lot ID: {item.lotCode || '-'}</span>
            </div>
          </div>
        </div>

        <Row className="mt-2">
          <Col md={6}>
            <FormGroup className="mb-0">
              <Label for={`amount-${index}`} style={{ fontSize: '0.85rem' }}>
                Berat
                <span
                  className="mx-1 text-muted"
                  style={{ fontSize: '0.75rem' }}
                >
                  (Berat maksimal {formatAmount(maxAmount)} {item.unit || ''})
                </span>
              </Label>
              <PMBInputAmount
                id={`amount-${index}`}
                value={
                  currentTransferAmount === ''
                    ? ''
                    : formatAmount(currentTransferAmount)
                }
                onChange={(e) => onAmountChange(index, e.target.value)}
                name={`amount-${index}`}
                placeholder="Masukkan jumlah"
                testId={`tf-inv-form-amount-input-${index}`}
              />
              {isOverMax && (
                <small className="text-danger d-block mt-1">
                  Jumlah melebihi stok yang tersedia (maksimal{' '}
                  {formatAmount(maxAmount)})
                </small>
              )}
              {isUnderMin && (
                <small className="text-danger d-block mt-1">
                  Jumlah harus lebih dari 0
                </small>
              )}
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup className="mb-0">
              <Label for={`unit-${index}`} style={{ fontSize: '0.85rem' }}>
                Satuan
              </Label>
              <Input
                id={`unit-${index}`}
                type="text"
                disabled
                value={item.unit || '-'}
                className="bg-light"
              />
            </FormGroup>
          </Col>
        </Row>
      </div>
    </div>
  )
}

TransferInventoryItem.propTypes = {
  item: PropTypes.any,
  index: PropTypes.number.isRequired,
  options: PropTypes.array,
  onSelect: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onAmountChange: PropTypes.func.isRequired,
  onGroupItemAmountChange: PropTypes.func.isRequired,
  onToggleGroup: PropTypes.func.isRequired,
  formatAmount: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
}

export default TransferInventoryItem
